import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import GemSellRules from "@/components/GemSellRules";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  errorText, formatPHP, formatWhen, maskMobile, METHOD_LABEL, newRequestId, normaliseKey, normaliseMobile, payoutFor,
  saleWords,
} from "@/lib/gems";

const FIELD = "mt-1 w-full min-w-0 border border-border rounded-sm px-3 py-2 bg-background";
const DEFAULT_MERCHANT = "The Gem Collector";
const DEFAULT_METHODS = [{ code: "gcash", label: "GCash" }, { code: "maya", label: "Maya" }];

// A 4xx means the server looked at this request and said no, so the same
// request_id must not be sent again (the server replays the row it already
// holds for an id it has seen). A network error or a 5xx is a maybe, and the
// retry must carry the SAME id so it can never become a second sale.
function isDefinitiveRefusal(err) {
  const status = err?.response?.status;
  return typeof status === "number" && status >= 400 && status < 500;
}

function LinkForm({ notice, onLinked }) {
  const [key, setKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const normal = normaliseKey(key);
  const ready = !!normal;

  const submit = async (e) => {
    e.preventDefault();
    if (!ready || busy) return;
    setBusy(true);
    setError("");
    try {
      // One try only: every verify spends one of the account's attempts, so a
      // refusal is shown and never retried.
      await api.post("/exchange/link", { key: normal });
      toast.success("Your ePlayQuest account is linked");
      await onLinked();
    } catch (err) {
      setError(errorText(err, "That did not work. Try again"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="v-card p-6 space-y-4" data-testid="gems-link-form">
      <h2 className="font-display text-xl font-bold text-primary">Link your ePlayQuest account</h2>
      <p className="text-sm text-muted-foreground">
        On ePlayQuest, open Gems and get a Treasure Chest Key. Enter it here. It works once, for 10 minutes.
      </p>
      {notice && <p className="text-sm">{notice}</p>}
      <label className="block text-sm">
        Treasure Chest Key
        <input name="key" value={key} autoComplete="off" autoCapitalize="characters" spellCheck={false}
          maxLength={20} placeholder="XXXX-XXXX-XXXX" className={FIELD} onChange={(e) => setKey(e.target.value)} />
      </label>
      {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
      <button type="submit" className="v-btn-primary" disabled={busy || !ready}>Link account</button>
    </form>
  );
}

// `fee` is THIS player's fee (the link view's next_fee_php: PHP 35 on a first
// sale, PHP 10 after, operator 2026-10-16), falling back to the config's.
function SellForm({ config, fee: feeIn, balance, onSold, onReview, onUnclear }) {
  const fee = Number.isInteger(feeIn) ? feeIn : config.fee_php;
  const [gems, setGems] = useState("");
  const [method, setMethod] = useState("gcash");
  const [mobile, setMobile] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  // One id per sell attempt: minted when the confirm opens, reused by a retry
  // or a double click, dropped the moment the player edits the sale or the
  // server definitively refuses it.
  const requestId = useRef(null);
  const inFlight = useRef(false);
  const quote = useMemo(() => payoutFor(gems, config, fee), [gems, config, fee]);
  const number = normaliseMobile(mobile);
  const overBalance = quote.ok && quote.gems > balance;
  const ready = !!config.enabled && quote.ok && !!number && !overBalance;
  const methods = config.methods && config.methods.length ? config.methods : DEFAULT_METHODS;
  const merchant = config.merchant_name || DEFAULT_MERCHANT;

  const edited = (setter) => (e) => {
    setter(e.target.value);
    setConfirming(false);
    setError("");
    requestId.current = null;
  };

  const review = async (e) => {
    e.preventDefault();
    if (!ready) return;
    // The server pays the CURRENT fee, so the confirm must quote it too.
    await onReview();
    // Back keeps the id; only an edit or a definitive refusal clears it, so
    // Back, Review, Confirm after an unclear failure can never become a
    // second sale.
    if (!requestId.current) requestId.current = newRequestId();
    setError("");
    setConfirming(true);
  };

  const confirm = async () => {
    if (!ready || inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setError("");
    if (!requestId.current) requestId.current = newRequestId();
    try {
      const { data } = await api.post("/exchange/sales", {
        gems: quote.gems, method, mobile_number: number, request_id: requestId.current,
        expected_fee_php: fee,
      });
      requestId.current = null;
      const words = saleWords(data);
      if (data && (data.state === "failed" || data.state === "rejected")) toast.error(words);
      else toast.success(`Sale received. ${words}`);
      setConfirming(false);
      setGems("");
      await onSold();
    } catch (err) {
      if (isDefinitiveRefusal(err)) {
        requestId.current = null;
        setError(errorText(err, "The sale did not go through"));
        // The server named a different fee: re-read it so the next review quotes it.
        if (err?.response?.data?.detail?.code === "FEE_CHANGED") { setConfirming(false); await onReview(); }
      } else {
        // The sale may have been made. Keep the id, say so, and reload the
        // history so a sale that landed shows up.
        setError("We could not confirm whether the sale was made. Check Your sales below, or confirm again to retry safely.");
        await onUnclear();
      }
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  return (
    <form onSubmit={review} className="space-y-4 pt-2" data-testid="gems-sell-form">
      <p className="text-sm text-muted-foreground">
        You are selling your gems to {merchant}. It pays you in pesos, less a {formatPHP(fee)} fee on this sale.
      </p>
      <label className="block text-sm">
        Gems to sell
        <input name="gems" value={gems} inputMode="numeric" className={FIELD} onChange={edited(setGems)} />
      </label>
      <label className="block text-sm">
        Send the pesos to
        <select name="method" value={method} className={FIELD} onChange={edited(setMethod)}>
          {methods.map((m) => <option key={m.code} value={m.code}>{m.label}</option>)}
        </select>
      </label>
      <label className="block text-sm">
        GCash or Maya number
        <input name="mobile" value={mobile} inputMode="tel" placeholder="09XXXXXXXXX" className={FIELD}
          onChange={edited(setMobile)} />
      </label>
      <p className="font-semibold" data-testid="gems-payout">
        {overBalance
          ? `You have only ${balance.toLocaleString("en-PH")} gems on ePlayQuest`
          : quote.ok ? `You get ${formatPHP(quote.payout)} after the ${formatPHP(fee)} fee` : quote.reason}
      </p>
      {!confirming && <button type="submit" className="v-btn-primary w-full sm:w-auto" disabled={!ready}>Review sale</button>}
      {confirming && ready && (
        <div className="v-card p-4 space-y-3" data-testid="gems-confirm">
          <p>
            Sell {quote.gems.toLocaleString("en-PH")} gems to {merchant} and receive {formatPHP(quote.payout)} in{" "}
            {METHOD_LABEL[method]} {maskMobile(number)}?
          </p>
          <p className="text-sm text-muted-foreground">
            {quote.gems.toLocaleString("en-PH")} gems minus the {formatPHP(fee)} fee is {formatPHP(quote.payout)}.
          </p>
          <div className="flex flex-wrap gap-2">
            <button type="button" className="v-btn-primary" onClick={confirm} disabled={busy}>Confirm sale</button>
            <button type="button" onClick={() => setConfirming(false)} disabled={busy}>Back</button>
          </div>
        </div>
      )}
      {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
    </form>
  );
}

function LinkedPanel({ link, config, onSold, onReview, onUnlink }) {
  const player = link.player;
  const open = !!(config && config.enabled);
  return (
    <section className="v-card p-6 space-y-3" data-testid="gems-linked">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="min-w-0 break-words">Linked to ePlayQuest player <strong>{link.display_name}</strong></p>
        <button type="button" className="text-sm underline" onClick={onUnlink}>Unlink</button>
      </div>
      {player ? (
        <p className="text-sm text-muted-foreground">
          You have {Number(player.balance_gems || 0).toLocaleString("en-PH")} gems on ePlayQuest.
        </p>
      ) : (
        <p className="text-sm">{link.player_error || "ePlayQuest cannot be reached right now."}</p>
      )}
      {open && player && !player.has_purchased && (
        <p data-testid="gems-buy-first">
          Buy gems on ePlayQuest at least once before you can sell. After one purchase you can sell your whole
          balance, winnings included.
        </p>
      )}
      {open && player && player.has_purchased && player.turnover_remaining > 0 && (
        <p data-testid="gems-turnover">
          Play {Number(player.turnover_remaining).toLocaleString("en-PH")} more gems on ePlayQuest before you can
          sell. Every gem you buy must be played once first.
        </p>
      )}
      {config && <GemSellRules config={config} />}
      {open && player && player.has_purchased && !(player.turnover_remaining > 0) && (
        <SellForm config={config} fee={link.next_fee_php} balance={Number(player.balance_gems || 0)}
          onSold={onSold} onReview={onReview} onUnclear={onSold} />
      )}
    </section>
  );
}

function History({ sales }) {
  return (
    <section className="v-card p-6">
      <h2 className="font-display text-lg font-bold text-primary">Your sales</h2>
      {sales.length === 0 ? (
        <p className="mt-2 text-sm text-muted-foreground">You have not sold any gems yet.</p>
      ) : (
        <ul className="mt-3 divide-y divide-border text-sm" data-testid="gems-history">
          {sales.map((s) => (
            <li key={s.id} className="py-3 space-y-1">
              <div className="flex flex-wrap items-baseline justify-between gap-x-3">
                <span className="font-semibold">
                  {Number(s.gems).toLocaleString("en-PH")} gems for {formatPHP(s.payout_php)}
                </span>
                <span className="text-muted-foreground">{formatWhen(s.created_at)}</span>
              </div>
              <div className="text-muted-foreground break-words">
                To {METHOD_LABEL[s.method] || s.method} {s.mobile_masked}
              </div>
              <div className="break-words">{saleWords(s)}{s.reject_reason ? `: ${s.reject_reason}` : ""}</div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export default function GemExchange() {
  const [config, setConfig] = useState(null);
  const [link, setLink] = useState(null);
  const [sales, setSales] = useState(null);
  const [loadError, setLoadError] = useState("");

  const load = useCallback(async () => {
    try {
      const [c, l, s] = await Promise.all([
        api.get("/exchange/config"), api.get("/exchange/link"), api.get("/exchange/sales/mine"),
      ]);
      setConfig(c.data);
      setLink(l.data);
      setSales(s.data || []);
      setLoadError("");
    } catch (err) {
      setLoadError(errorText(err, "The Gem Exchange could not be loaded. Try again shortly"));
    }
  }, []);

  // Pressing Review re-reads the fees and THIS player's next fee (the link
  // view), so the confirm shows what the server will pay.
  const refreshConfig = useCallback(async () => {
    try {
      const [c, l] = await Promise.all([api.get("/exchange/config"), api.get("/exchange/link")]);
      if (c.data) setConfig(c.data);
      if (l.data) setLink(l.data);
    } catch (err) {
      // keep the config already on screen; the server still enforces its own
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const unlink = async () => {
    try {
      await api.delete("/exchange/link");
      toast.success("Your ePlayQuest account is unlinked");
    } catch (err) {
      toast.error(errorText(err, "Could not unlink"));
    }
    await load();
  };

  const open = !!(config && config.enabled);

  return (
    <DashboardLayout title="Sell your gems" subtitle="Sell ePlayQuest gems to The Gem Collector for pesos in GCash or Maya">
      <div className="max-w-3xl w-full space-y-6">
        {loadError && <p role="alert" className="text-destructive">{loadError}</p>}
        {config && !config.enabled && (
          <div className="v-card p-4" data-testid="gems-closed">
            The Gem Exchange is not open yet. Linking your account and selling gems will open together.
          </div>
        )}
        {open && link && !link.linked && <LinkForm notice={link.notice} onLinked={load} />}
        {link && link.linked && <LinkedPanel link={link} config={config} onSold={load} onReview={refreshConfig} onUnlink={unlink} />}
        {sales !== null && <History sales={sales} />}
      </div>
    </DashboardLayout>
  );
}
